import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Button, Menu, MenuItem, ThemeProvider } from '@mui/material'
import { useSnackbar } from 'notistack'

import { useGetCustomerQuery } from '@/app/services/customer'
import { useCreateOrderMaterialListMutation } from '@/app/services/materials'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectOrganisation, selectOrganisationId, selectPaidFeatures } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { theme as darkTheme } from '@/common/themes/DarkTheme'
import { StoreThemeOverride } from '@/common/themes/overrides/StoreThemeOverride'
import { ORDER_ADD_PARTS_OPTIONS } from '@/common/utils'

import Add3DPartsDialog from '../common/Add3DPartsDialog'
import AddFromPartLibrary from '../common/AddFromPartLibrary'
import AddOrderItemsDialog from '../common/AddOrderItemsDialog'
import AddPDFPartsDialog from '../common/AddPDFParts'
import AddQuickPartDialog from '../common/AddQuickPartDialog'

const AddPartsButtonGroup = () => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const { showTube } = useToolBoxTreatments()

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const paidFeatures = useSelector(selectPaidFeatures)

    const currentCustomerId = useSelector(selectCustomerId)

    const { data: currentCustomer } = useGetCustomerQuery(
        { organisationId, customerId: currentCustomerId },
        { skip: !currentCustomerId }
    )

    const [createOrderMaterialList] = useCreateOrderMaterialListMutation()

    const [interfaceToShow, setInterfaceToShow] = useState('')
    const [anchorEl, setAnchorEl] = useState(null)

    // `?? true`, not `&&`: QM-3520 (backend) hasn't shipped this field yet, so every current
    // API response omits it — falling back to `&&` would hide 2D uploads and Quick Part for
    // every Web Store the moment this ships, before the backend flag exists. One flag covers
    // both: a Quick Part is a 2D part, so hiding 2D uploads hides Quick Part too.
    const is2dUploadEnabled = storeSettings?.webStore2dEnabled ?? true
    const isPartLibraryEnabled = storeSettings?.webStorePartLibraryEnabled && paidFeatures?.hasPartLibrary
    const isUnfoldTubeEnabled = storeSettings?.webStoreTubeEnabled && paidFeatures?.hasUnfoldTube && showTube
    const is3dEnabled = storeSettings?.webStore3dEnabled && paidFeatures?.has3d
    const isPDFUploadEnabled = storeSettings?.webStorePdfEnabled && paidFeatures?.hasPdfUpload

    const open = Boolean(anchorEl)

    const createMaterialList = async (organisationId, urlParams) => {
        try {
            await createOrderMaterialList({ organisationId, location: urlParams }).unwrap()
        } catch (error) {
            console.error(error)
        }
    }

    const handleAddPartsButtonClick = (event) => {
        setAnchorEl(event.currentTarget)
    }
    const handleAddPartsMenuClose = () => {
        setAnchorEl(null)
    }

    const resetInterface = () => {
        setInterfaceToShow('')
        handleAddPartsMenuClose()
    }

    const handleAdd2dPartsClick = () => {
        setInterfaceToShow('2d')
        handleAddPartsMenuClose()
    }

    const handleAddFromQuickPartClick = () => {
        setInterfaceToShow('quickpart')
        handleAddPartsMenuClose()
    }

    const handleAdd3dPartsClick = useCallback(() => {
        createMaterialList(organisationId, 'Unfold3d').then(() => {
            setInterfaceToShow('3d')
            handleAddPartsMenuClose()
        })
    }, [organisationId])

    const handleAddRotaryPartsClick = useCallback(() => {
        createMaterialList(organisationId, 'Unfold3d').then(() => {
            setInterfaceToShow('3d-tube')
            handleAddPartsMenuClose()
        })
    }, [organisationId])

    const handleAddPdfPartsClick = useCallback(() => {
        createMaterialList(organisationId, 'Pdf2Quote').then(() => {
            setInterfaceToShow('pdf')
            handleAddPartsMenuClose()
        })
    }, [organisationId])

    const handleAddFromPartsLibraryClick = () => {
        try {
            setInterfaceToShow('partlibrary')
            handleAddPartsMenuClose()
        } catch (err) {
            enqueueSnackbar(t('$t(An error occurred), failed to add parts from the library.'), {
                variant: 'error',
            })
        }
    }

    const resolvedDarkTheme = useMemo(
        () => (StoreThemeOverride ? StoreThemeOverride(darkTheme, storeSettings) : darkTheme),
        [StoreThemeOverride, darkTheme, storeSettings]
    )

    const menuItems = useMemo(() => {
        return ORDER_ADD_PARTS_OPTIONS?.map((option) => {
            const { handler, id, label, tagAttrs } = option
            let handleOptionClick
            switch (handler) {
                case 'handleAdd2dParts':
                    handleOptionClick = handleAdd2dPartsClick
                    break
                case 'handleAdd3dParts':
                    handleOptionClick = handleAdd3dPartsClick
                    break
                case 'handleAddRotaryParts':
                    handleOptionClick = handleAddRotaryPartsClick
                    break
                case 'handleAddFromQuickParts':
                    handleOptionClick = handleAddFromQuickPartClick
                    break
                case 'handleAddPdfParts':
                    handleOptionClick = handleAddPdfPartsClick
                    break
                case 'handleAddFromPartsLibrary':
                    handleOptionClick = handleAddFromPartsLibraryClick
                    break

                default:
                    break
            }
            return {
                id,
                label,
                handler,
                tagAttrs,
                handleOptionClick,
            }
        })
    }, [ORDER_ADD_PARTS_OPTIONS])

    const isMenuItemHidden = (menuItemId) => {
        if (menuItemId === 'add2dParts') return !is2dUploadEnabled
        if (menuItemId === 'add3dParts') return !is3dEnabled
        if (menuItemId === 'addRotaryParts') return !isUnfoldTubeEnabled
        if (menuItemId === 'addFromQuickParts') return !is2dUploadEnabled
        if (menuItemId === 'addPdfParts') return !isPDFUploadEnabled
        if (menuItemId === 'addFromPartsLibrary') return !isPartLibraryEnabled
    }

    const isMenuItemDisabled = (menuItemId) => {
        if (menuItemId === 'addFromPartsLibrary') return !currentCustomer
    }

    const menuItemDisabledMessage = (menuItemId) => {
        if (menuItemId === 'addFromPartsLibrary') {
            return t('Log in to use this feature')
        }
    }

    return (
        <>
            <Button
                aria-controls={open ? 'add-parts-button-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                color="primary"
                endIcon={
                    <HugeiconsIcon
                        icon={ArrowDown01Icon}
                        size={18}
                    />
                }
                id="add-parts-button"
                size="large"
                variant="contained"
                disableElevation
                onClick={handleAddPartsButtonClick}
            >
                {t('Add parts')}
            </Button>
            <Menu
                anchorEl={anchorEl}
                id="order-actions-button-menu"
                MenuListProps={{
                    'aria-labelledby': 'order-actions-button',
                }}
                open={open}
                onClose={handleAddPartsMenuClose}
            >
                {menuItems?.map((menuItem) =>
                    !isMenuItemHidden(menuItem?.id) ? (
                        isMenuItemDisabled(menuItem?.id) ? (
                            <TbxTooltip
                                key={`${menuItem?.id}-tooltip`}
                                title={menuItemDisabledMessage(menuItem?.id)}
                                arrow
                            >
                                <span>
                                    <MenuItem
                                        disabled={isMenuItemDisabled(menuItem?.id)}
                                        key={menuItem?.id}
                                        {...menuItem?.tagAttrs}
                                    >
                                        {t(menuItem?.label)}
                                    </MenuItem>
                                </span>
                            </TbxTooltip>
                        ) : (
                            <MenuItem
                                key={menuItem?.id}
                                {...menuItem?.tagAttrs}
                                onClick={() => menuItem?.handleOptionClick()}
                            >
                                {t(menuItem?.label)}
                            </MenuItem>
                        )
                    ) : null
                )}
            </Menu>

            <AddOrderItemsDialog
                open={interfaceToShow === '2d'}
                onClose={resetInterface}
            />

            <ThemeProvider theme={resolvedDarkTheme}>
                <Add3DPartsDialog
                    is3DTube={interfaceToShow === '3d-tube'}
                    open={interfaceToShow === '3d' || interfaceToShow === '3d-tube'}
                    onClose={resetInterface}
                />

                <AddQuickPartDialog
                    open={interfaceToShow === 'quickpart'}
                    onClose={resetInterface}
                />

                <AddPDFPartsDialog
                    open={interfaceToShow === 'pdf'}
                    onClose={resetInterface}
                />

                <AddFromPartLibrary
                    customer={currentCustomer}
                    open={interfaceToShow === 'partlibrary'}
                    onClose={resetInterface}
                />
            </ThemeProvider>
        </>
    )
}

export default AddPartsButtonGroup
